import { ApiError, type StoreTicketDetail, type StoreTicketType } from "@khazzin/api-client";
import { type TicketFile, TicketFiles } from "@khazzin/ui/patterns";
import { MoneyInput } from "@khazzin/ui/patterns/form";
import { Button, Drawer, Field, Select, Textarea } from "@khazzin/ui/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { useStoreScope } from "../../app/shell/scope";
import { isClaim, type TicketSource, ticketsKey, typesFor } from "./tickets";

/**
 * Open a ticket (EP10-S12, DA-10-11): the type — only the ones the source
 * offers and the account may open — a description, files, and for a claim the
 * amount. Opened, it goes to the ticket.
 */
export interface OpenTicketDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	source: TicketSource;
	/** The linked record's id; absent for a general question. */
	linkedId?: string;
	/** What the drawer names it by: the order number. */
	linkedLabel?: string;
	/** A first line to start the description with. */
	prefill?: string;
}

export function OpenTicketDrawer({
	open,
	onOpenChange,
	source,
	linkedId,
	linkedLabel,
	prefill = "",
}: OpenTicketDrawerProps) {
	const { t } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { store } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const types = typesFor(store, source);
	const [type, setType] = useState<StoreTicketType>(types[0] ?? "question");
	const [description, setDescription] = useState(prefill);
	const [claimed, setClaimed] = useState<number | null>(null);
	const [files, setFiles] = useState<TicketFile[]>([]);
	const [error, setError] = useState<{ message: string; field?: string } | null>(null);
	const [sending, setSending] = useState(false);
	const claim = isClaim(type);
	const ready = description.trim() !== "" && (!claim || (claimed ?? 0) > 0) && !sending;

	const submit = () => {
		setSending(true);
		setError(null);
		api
			.openPortalTicket({
				type,
				description: description.trim(),
				...(source === "none" || linkedId === undefined
					? {}
					: { linkedEntity: { kind: source, id: linkedId } }),
				...(claim && claimed !== null ? { claimedMinor: claimed } : {}),
				...(files.length > 0 ? { attachmentIds: files.map((f) => f.id) } : {}),
			})
			.then(
				(opened: StoreTicketDetail) => {
					void queryClient.invalidateQueries({ queryKey: ticketsKey(storeId) });
					onOpenChange(false);
					void navigate(`/tickets/${encodeURIComponent(opened.ticket.id)}`);
				},
				(cause: unknown) =>
					setError(
						cause instanceof ApiError
							? { message: cause.message, ...(cause.field ? { field: cause.field } : {}) }
							: { message: t("portal.tickets.open.failed") },
					),
			)
			.finally(() => setSending(false));
	};

	return (
		<Drawer
			open={open}
			onOpenChange={onOpenChange}
			title={t("portal.tickets.open.title")}
			{...(linkedLabel === undefined
				? {}
				: {
						description: t("portal.tickets.open.about", {
							kind: t(`tickets.link.${source}`),
							label: linkedLabel,
						}),
					})}
			closeLabel={t("ui.actions.close")}
			footer={
				<>
					<Button variant="secondary" onClick={() => onOpenChange(false)}>
						{t("ui.actions.cancel")}
					</Button>
					<Button variant="primary" disabled={!ready} onClick={submit}>
						{t("portal.tickets.open.submit")}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-4">
				<Field label={t("portal.tickets.fields.type")} required>
					{(control) => (
						<Select
							{...control}
							value={type}
							onValueChange={(next) => setType(next as StoreTicketType)}
							options={types.map((value) => ({ value, label: t(`tickets.type.${value}`) }))}
						/>
					)}
				</Field>
				<Field
					label={t("portal.tickets.fields.description")}
					required
					{...(error?.field === "description" ? { error: error.message } : {})}
				>
					{(control) => (
						<Textarea
							{...control}
							rows={5}
							maxLength={4000}
							value={description}
							onChange={(event) => setDescription(event.target.value)}
						/>
					)}
				</Field>
				{claim && (
					<Field
						label={t("portal.tickets.fields.claimed")}
						hint={t("portal.tickets.fields.claimedHint")}
						required
						{...(error?.field === "claimedAmount" ? { error: error.message } : {})}
					>
						{(control) => (
							<MoneyInput
								{...control}
								currency="USD"
								locked
								min={1}
								value={claimed}
								onChange={setClaimed}
							/>
						)}
					</Field>
				)}
				<TicketFiles api={api} storeId={storeId} files={files} onChange={setFiles} t={t} />
				{error !== null && error.field !== "description" && error.field !== "claimedAmount" && (
					<p role="alert" className="text-[12.5px] text-danger-fg">
						{error.message}
					</p>
				)}
			</div>
		</Drawer>
	);
}

/**
 * Report a problem (EP10-S12): the drawer, launched from a record's page with
 * the record prefilled. Absent when the account could open nothing from it.
 */
export function ReportProblemButton({
	source,
	id,
	label,
}: {
	source: Exclude<TicketSource, "none">;
	id: string;
	label: string;
}) {
	const { t } = useLocale();
	const { store } = useStoreScope();
	const [open, setOpen] = useState(false);
	if (typesFor(store, source).length === 0) {
		return null;
	}
	return (
		<>
			<Button variant="secondary" onClick={() => setOpen(true)}>
				{t("portal.tickets.report")}
			</Button>
			{open && (
				<OpenTicketDrawer
					open
					onOpenChange={setOpen}
					source={source}
					linkedId={id}
					linkedLabel={label}
				/>
			)}
		</>
	);
}
